import React, { useEffect, useState } from 'react';
import FaiBox from "../../../../../MuiComponents/FaiBox";
import FaiTypography from "../../../../../MuiComponents/FaiTypography";
import theme from "../../../../../assets/theme";
import {Form, Formik, FormikProps} from "formik";
import {Grid, Snackbar, Alert} from '@mui/material';
import FaiSelect from "../../../../../MuiComponents/FaiSelect";
import FormField from "../../../../../components/FormField";
import FaiButton from "../../../../../MuiComponents/FaiButton";
import { merchantAuthController } from '../../../../../controllers/authController';
import { ProductController } from '../../../../../controllers/productController';
import SalesController from '../../../../../controllers/saleController';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';

interface InitialObject {
    brand: string;
    products: string;
    campaignName: string;
    description: string;
    startDate: string;
    endDate: string;
    discountType: string;
    discount: number;
}

// Backend `Utils.parseLocalDate` expects dd-MM-yyyy; the <input type="date"> emits yyyy-MM-dd.
const toBackendDate = (iso: string): string => {
    if (!iso) return iso;
    const [y, m, d] = iso.split('-');
    return `${d}-${m}-${y}`;
};

function CreateSale() {
    const {typography, palette, borders} = theme;
    const [brand, setBrands] = useState([]);
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const navigate = useNavigate();

    const [initialObject, setInitialObject] = React.useState<InitialObject>({
        brand: "",
        products: "",
        campaignName: "",
        description: "",
        startDate: "",
        endDate: "",
        discountType: "",
        discount: 0,
    })

    const validationSchema = Yup.object().shape({
        brand: Yup.string().required('Brand is required'),
        products: Yup.string().required('Product is required'),
        campaignName: Yup.string().required('Campaign name is required'),
        description: Yup.string().required('Description is required'),
        startDate: Yup.string().required('Start date is required'),
        endDate: Yup.string().required('End date is required'),
        discountType: Yup.string().required('Discount type is required'),
        discount: Yup.number().required('Discount is required').min(1, 'Discount must be greater than 0'),
    });

    useEffect(() => {
            const fetchBrands = async () => {
                try {
                    const apiResponse: any = await merchantAuthController.getBrandList();
                    console.log('Brand List:', apiResponse.data.data.data);
                    const categoryOptions = apiResponse.data.data.data.map(category => ({
                        value: category.brandName,
                        label: category.brandName
                    }));
                    setBrands(categoryOptions);
                } catch (error) {
                    console.error('Error fetching coupons:', error);
                } finally {
                    setLoading(false);
                }
            };
            fetchBrands();
        }, []);

    const brandCategory = async (brandName: string) => {
        try {
            setLoading(true);
            const response = await ProductController.getMasterCategoriesByBrand(brandName);
            
            if (response.data && response.data.data && response.data.data.masterCategories) {
                const stateOptions = response.data.data.masterCategories.map((state: any) => ({
                    value: state.name,
                    label: state.name,
                    id: state.id
                }));
                setStates(stateOptions);
            }
        } catch (error) {
            console.error('Error fetching states:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values: any, { setSubmitting, setFieldError }: any) => {
        try {
            setSubmitLoading(true);

            // `products` holds the master category *name*; look up its id from the loaded options.
            const selectedCategory = states.find((s: any) => s.value === values.products) as any;
            const masterCategoryId = selectedCategory?.id;
            if (!masterCategoryId) {
                setFieldError('products', 'Please select a valid category');
                return;
            }

            // Backend uses @ModelAttribute with a MultipartFile media field, so we must submit
            // multipart/form-data. List fields are added by repeating the same key.
            const formData = new FormData();
            formData.append('brand', values.brand);
            formData.append('campaignName', values.campaignName);
            formData.append('description', values.description);
            formData.append('saleType', values.discountType);
            formData.append('percentageFlat', String(values.discount));
            formData.append('validFromDate', toBackendDate(values.startDate));
            formData.append('validTillDate', toBackendDate(values.endDate));
            formData.append('validFromTime', '00:00');
            formData.append('validTillTime', '23:59');
            formData.append('masterCategoryIds', masterCategoryId);

            const response = await SalesController.createSale(formData);
            setSnackbarMessage((response.data as any)?.message || 'Sale created successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            // Let the user see the toast briefly before leaving the page.
            setTimeout(() => navigate('/app/promotions/sales'), 1200);
        } catch (error: any) {
            console.error('Error creating sale:', error);
            const message =
                error.response?.data?.message ||
                error.response?.data?.errors?.[0] ||
                'Failed to create sale. Please try again.';
            setSnackbarMessage(message);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            if (error.response?.data?.message) {
                setFieldError('general', error.response.data.message);
            }
        } finally {
            setSubmitLoading(false);
            setSubmitting(false);
        }
    };

    return (
        <>
            <FaiBox>
                <FaiBox className={"tab-title"} mb={"21px"}>
                    <FaiTypography variant={"h5"} fontWeight={typography.fontWeightMedium}>
                        Create custom sale
                    </FaiTypography>
                </FaiBox>
                <FaiBox bgColor={palette.white.main} p={"15px"}
                        borderRadius={borders.borderRadius.md}>
                    <Formik 
                        initialValues={initialObject} 
                        enableReinitialize={true} 
                        validateOnBlur={true}
                        validateOnChange={true} 
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >

                        {(props: FormikProps<InitialObject>) => {
                            const {
                                handleSubmit,
                                setFieldValue,
                                handleChange,
                                handleBlur,
                                values,
                                touched,
                                isValid,
                                errors
                            } = props

                            return (
                                <>
                                    <Form>
                                        <FaiBox mb={3}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <FaiBox mb={3}>
                                                        <FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                            <FaiTypography
                                                                component="label"
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                textTransform="capitalize"
                                                            >
                                                                Brand Name
                                                            </FaiTypography>
                                                        </FaiBox>
                                                        <FaiSelect
                                                            placeholder={"Select Brand"}
                                                            name={"brand"}
                                                            options={brand}
                                                            onChange={(selectedOption: any) => {
                                                                setFieldValue("brand", selectedOption.value);
                                                                brandCategory(selectedOption.value);
                                                            }}
                                                        />
                                                    </FaiBox>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FaiBox mb={3}>
                                                        <FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                            <FaiTypography
                                                                component="label"
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                textTransform="capitalize"
                                                            >
                                                                Choose Products
                                                            </FaiTypography>
                                                        </FaiBox>
                                                        <FaiSelect
                                                            placeholder={"Choose Products"}
                                                            name={"products"}
                                                            options={states}
                                                            onChange={(selectedOption: any) => {
                                                                setFieldValue("products", selectedOption.value);
                                                            }}
                                                        />
                                                    </FaiBox>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormField
                                                        label={"Campaign Name"}
                                                        name={"campaignName"}
                                                        placeholder={"Enter campaign name"}
                                                        value={values.campaignName}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.campaignName && Boolean(errors.campaignName)}
                                                        helperText={touched.campaignName && errors.campaignName}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormField
                                                        label={"Description"}
                                                        name={"description"}
                                                        placeholder={"Enter description"}
                                                        value={values.description}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.description && Boolean(errors.description)}
                                                        helperText={touched.description && errors.description}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormField
                                                        type={"date"}
                                                        label={"Start Date"}
                                                        name={"startDate"}
                                                        value={values.startDate}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.startDate && Boolean(errors.startDate)}
                                                        helperText={touched.startDate && errors.startDate}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormField
                                                        type={"date"}
                                                        label={"End Date"}
                                                        name={"endDate"}
                                                        value={values.endDate}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.endDate && Boolean(errors.endDate)}
                                                        helperText={touched.endDate && errors.endDate}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FaiBox mb={1}>
                                                        <FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                            <FaiTypography
                                                                component="label"
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                textTransform="capitalize"
                                                            >
                                                                Discount Type
                                                            </FaiTypography>
                                                        </FaiBox>
                                                        <FaiSelect
                                                            placeholder={"Choose Type"}
                                                            name={"discountType"}
                                                            options={[
                                                                {value: "FLAT", label: "Flat"},
                                                                {value: "PERCENTAGE", label: "Percentage"},
                                                            ]}
                                                            onChange={(selectedOption: any) => {
                                                                setFieldValue("discountType", selectedOption.value);
                                                            }}
                                                        />
                                                    </FaiBox>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormField
                                                        label={"Discount"}
                                                        name={"discount"}
                                                        placeholder={"Enter percentage"}
                                                        value={values.discount}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.discount && Boolean(errors.discount)}
                                                        helperText={touched.discount && errors.discount}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </FaiBox>
                                        <Grid container>
                                            <Grid item sm={4}>
                                                <FaiButton
                                                    color="primary"
                                                    variant={"contained"}
                                                    fullWidth
                                                    xs={{ fontWeight: typography.fontWeightMedium }}
                                                    type="submit"
                                                    disabled={submitLoading || !isValid}
                                                >
                                                    {submitLoading ? 'Creating...' : 'Create Sale'}
                                                </FaiButton>
                                            </Grid>
                                            <Grid item sm={6}></Grid>
                                            <Grid item sm={2}>
                                                <FaiBox>
                                                    <FaiButton
                                                        color="text"
                                                        variant={"outlined"}
                                                        fullWidth
                                                    >
                                                        Cancel
                                                    </FaiButton>
                                                </FaiBox>
                                            </Grid>
                                        </Grid>
                                    </Form>

                                </>
                            )
                        }}
                    </Formik>
                </FaiBox>
            </FaiBox>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

export default CreateSale;
